import { useState, useRef, useEffect } from "react";
import videojs from "video.js";
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil";
import { useInView } from "react-intersection-observer";

import { EmbeddablePlayerViewModel } from "./ViewModel";
import { buildIframeOptions, isFaceItExpiredClip } from "./utils";
import { EmbeddablePlayerWrapper } from "./styledComponents";
import { Loading } from "./Components/Loading";
import { Pruned } from "./Components/Pruned";
import { Overlay } from "./Components/Overlay";
import { Pending } from "./Components/Pending";
import { Error } from "./Components/Error";
import { ActivePlaylistClipState } from "MVVM/State/embeddablePlaylist";
import {
  ActiveOverlayPopUpState,
  PlatformState,
  ShouldDisplayOverlayState,
  TrackedFirstHoverState,
} from "MVVM/State/iframe";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { countClipImpression } from "MVVM/Utilities/Clip";
import { TUserRole } from "./@types";

interface IEmbeddablePlayerProps {
  userRole?: TUserRole;
  className?: string;
  onPlayEndVariant?: "history" | "nextUp" | "nextUpPlaylist";
}

const EmbeddablePlayer = ({
  userRole = "creator",
  className,
  onPlayEndVariant = "history",
}: IEmbeddablePlayerProps) => {
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.5 });

  const [trackedOverlayDisplayed, setTrackedOverlayDisplayed] = useState(false);
  const [trackedFirstHover, setTrackedFirstHover] = useRecoilState(
    TrackedFirstHoverState,
  );
  const [shouldDisplayOverlay, setShouldDisplayOverlay] = useRecoilState(
    ShouldDisplayOverlayState,
  );
  const [hasAutoDisplayedHistoryPopup, setHasAutoDisplayedHistoryPopup] =
    useState(false);
  const [autoplayState, setAutoplayState] = useState(false);
  const [mutedState, setMutedState] = useState(false);
  const [mouseInactivityTimer, setMouseInactivityTimer] =
    useState<NodeJS.Timeout | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const platform = useRecoilValue(PlatformState);

  const options = buildIframeOptions();
  const { data, loading, error, refetchClip } = EmbeddablePlayerViewModel();
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );

  const activePlaylistClip = useRecoilValue(ActivePlaylistClipState);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const background = params.get("background");
    const muted = params.get("muted");
    const shouldMute =
      muted === "false" && !autoplayState ? false : autoplayState;
    const autoplay = params.get("autoplay");
    const shouldAutoplay =
      [autoplay, background].includes("true") || autoplay !== "false";

    setAutoplayState(shouldAutoplay);
    setMutedState(shouldMute);
  }, [autoplayState]);

  useEffect(() => {
    if (!videoPlayerRef.current) return;

    if (inView) {
      videoPlayerRef.current.play();
      setAutoplayState(true);
    } else {
      videoPlayerRef.current.pause();
    }
  }, [inView]);

  useEffect(() => {
    if (!inView) return;

    setShouldDisplayOverlay(true);

    const timeout = setTimeout(() => {
      setShouldDisplayOverlay(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [activePlaylistClip, inView, setShouldDisplayOverlay]);

  const resetMouseInactivityTimer = () => {
    if (mouseInactivityTimer) {
      clearTimeout(mouseInactivityTimer);
    }

    const timer = setTimeout(() => {
      if (isVideoPlaying) {
        setShouldDisplayOverlay(false);
      }
    }, 2000);

    setMouseInactivityTimer(timer);
  };

  useEffect(() => {
    return () => {
      if (mouseInactivityTimer) {
        clearTimeout(mouseInactivityTimer);
      }
    };
  }, [mouseInactivityTimer]);

  const handlePlayerReady = (player?: videojs.Player | null) => {
    if (player) {
      player.ready(() => {
        const controlBar = player.controlBar.el();

        if (!controlBar.querySelector(".vjs-control-bar-brand")) {
          const img = document.createElement("img");

          img.onclick = () => {
            const utmSource =
              data?.clip?.developer?.slug ||
              new URLSearchParams(window.location.search).get("platform") ||
              "unknown";

            const ajs_aid = window.rudderanalytics.getAnonymousId();
            track("Button - Click", {
              locationIdentifier: "allstar_logo",
              location: "embeddable_player_overlay",
            });
            window.open(
              `${window.location.origin}?utm_source=${utmSource}&ajs_aid=${ajs_aid}`,
              "_blank",
            );
          };

          const imgSrc = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/allstargg.png`;
          img.src = imgSrc;
          img.className = "vjs-control-bar-brand";

          controlBar.appendChild(img);
        }
      });
    }
  };
  const { track, analyticsProperties } = useAnalyticsContext({
    clip: data?.clip,
  });

  useEffect(() => {
    if ("ReportingObserver" in window) {
      const observer = new ReportingObserver((reports) => {
        reports.forEach((report) => {
          if (
            report.type === "intervention" &&
            report.body &&
            (report.body as any).id === "HeavyAdIntervention"
          ) {
            track("Heavy Ad Intervention", {
              reason: (report.body as any).message || "Heavy ad intervention",
              clipId: data?.clip?.internalId,
            });
          }
        });
      });
      observer.observe();

      return () => observer.disconnect();
    }
  }, [track, data?.clip?.internalId]);

  useEffect(() => {
    setHasAutoDisplayedHistoryPopup(false);
    setTrackedOverlayDisplayed(false);
  }, [data?.clip?.internalId]);

  useEffect(() => {
    if (shouldDisplayOverlay && !trackedOverlayDisplayed) {
      track("Embeddable Player Overlay Displayed");
      setTrackedOverlayDisplayed(true);
    }
  }, [shouldDisplayOverlay, trackedOverlayDisplayed, track]);

  if (loading) return <Loading />;

  const clip = data?.clip;

  const clipPartnerDoesNotMatchPlatform =
    clip?.developer?.slug && platform && clip?.developer?.slug !== platform;

  if (
    error ||
    !clip ||
    clipPartnerDoesNotMatchPlatform ||
    isFaceItExpiredClip({ clip, platform })
  )
    return <Error />;
  if (["PRUNED", "ON_DEMAND"].includes(clip.status))
    return (
      <Pruned
        thumbnail={clip.thumbnailStandardUrl}
        clip={clip}
        refetchClip={refetchClip}
      />
    );
  if (["PENDING", "ERROR"].includes(clip.status))
    return <Pending clip={clip} refetchClip={refetchClip} />;

  const getOnPlayEnd = (variant: string) => () => {
    setShouldDisplayOverlay(true);
    setIsVideoPlaying(false);
    resetMouseInactivityTimer();
    if (variant) setActiveOverlayPopUp(variant);
  };

  const handlePlayEnd = getOnPlayEnd(onPlayEndVariant);

  return (
    <EmbeddablePlayerWrapper
      ref={inViewRef}
      onMouseOver={() => {
        setShouldDisplayOverlay(true);
        resetMouseInactivityTimer();
        const searchParams = new URLSearchParams(window.location.search);
        const playlistParam = searchParams.get("playlist");

        if (!trackedFirstHover && !playlistParam) {
          track("Iframe First Hover", {
            locationIdentifier: "iframe_first_hover",
            location: "embeddable_player",
            platform,
          });
          setTrackedFirstHover(true);
        }
      }}
      onMouseMove={() => {
        setShouldDisplayOverlay(true);
        resetMouseInactivityTimer();
      }}
      onMouseLeave={() => {
        setShouldDisplayOverlay(false);
        if (mouseInactivityTimer) {
          clearTimeout(mouseInactivityTimer);
        }
      }}
      className={className}
      id="embeddable-player-wrapper"
    >
      <Overlay
        player={videoPlayerRef.current}
        clip={clip}
        userRole={userRole}
      />
      <VideoPlayer
        key={clip?.internalId}
        watchAnalyticsProperties={analyticsProperties}
        playAnalyticsProperties={analyticsProperties}
        mp4Source={clip.url}
        refForVideoPlayer={videoPlayerRef}
        wrapperId={"embeddable-player-wrapper"}
        options={{
          ...options,
          autoplay: autoplayState,
          muted: mutedState ? mutedState : true,
          poster:
            options.useOpenGraphImage && clip.thumbnailOverlayUrl
              ? clip.thumbnailOverlayUrl
              : clip.thumbnailStandardUrl,
        }}
        loop={options.shouldLoop}
        onPlayPause={() => {
          setShouldDisplayOverlay(true);
          setIsVideoPlaying((prev) => !prev);
          resetMouseInactivityTimer();
          if (!hasAutoDisplayedHistoryPopup && !activeOverlayPopUp) {
            setActiveOverlayPopUp("history");
            setHasAutoDisplayedHistoryPopup(true);
          }
        }}
        onPlayEnd={handlePlayEnd}
        onPlayStart={() => {
          setIsVideoPlaying(true);
          countClipImpression(clip.shareId);
        }}
        onPlay={() => {
          setIsVideoPlaying(true);
          setActiveOverlayPopUp(null);
        }}
        onReady={handlePlayerReady}
        className="embeddable-player"
      />
    </EmbeddablePlayerWrapper>
  );
};

export default EmbeddablePlayer;
